import {
  CopySimple, Trash, ArrowUp, ArrowDown,
  ArrowSquareIn, ArrowSquareOut, CornersOut,
} from '@phosphor-icons/react';
import {
  ContextMenuContent, ContextMenuItem, ContextMenuSeparator,
  ContextMenuSub, ContextMenuSubTrigger, ContextMenuSubContent, ContextMenuLabel,
} from '../ui/context-menu';
import { useSceneStore } from '../../stores/sceneStore';
import { getSpineManager } from '../../lib/spine-manager';
import type { Layer, LayerGroup } from '@spine-workspace/shared';

interface LayerContextMenuProps {
  layer: Layer;
  groups: LayerGroup[];
}

export default function LayerContextMenu({ layer, groups }: LayerContextMenuProps) {
  const {
    selectedLayerIds,
    duplicateLayer, removeLayer, moveLayerUp, moveLayerDown,
    setLayerGroup, pushUndo,
  } = useSceneStore();

  // Multi-select: if right-clicked layer is part of the current multi-selection
  const isMulti = selectedLayerIds.length > 1 && selectedLayerIds.includes(layer.id);
  const targetIds = isMulti ? selectedLayerIds : [layer.id];

  function handleRename() {
    const el = document.querySelector<HTMLElement>(`[data-rename-layer="${layer.id}"]`);
    el?.dispatchEvent(new Event('start-rename', { bubbles: true }));
  }

  function handleFocus() {
    getSpineManager().focusLayer(layer.id);
  }

  function handleDuplicate() {
    pushUndo();
    targetIds.forEach((id) => duplicateLayer(id));
  }

  function handleDelete() {
    targetIds.forEach((id) => removeLayer(id));
  }

  function handleMoveToGroup(groupId: string | null) {
    targetIds.forEach((id) => setLayerGroup(id, groupId));
  }

  return (
    <ContextMenuContent data-testid={`layer-ctx-menu-${layer.id}`}>
      <ContextMenuLabel>
        {isMulti ? `${targetIds.length} layers selected` : layer.name}
      </ContextMenuLabel>
      <ContextMenuSeparator />

      {/* Single-layer only actions */}
      {!isMulti && (
        <>
          <ContextMenuItem onClick={handleFocus}>
            <CornersOut size={13} className="text-text-tertiary" />
            Focus in viewport
          </ContextMenuItem>
          <ContextMenuItem onClick={handleRename}>
            Rename
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => { pushUndo(); moveLayerUp(layer.id); }}>
            <ArrowUp size={13} className="text-text-tertiary" />
            Move up
          </ContextMenuItem>
          <ContextMenuItem onClick={() => { pushUndo(); moveLayerDown(layer.id); }}>
            <ArrowDown size={13} className="text-text-tertiary" />
            Move down
          </ContextMenuItem>
          <ContextMenuSeparator />
        </>
      )}

      <ContextMenuSeparator />

      <ContextMenuItem onClick={handleDuplicate}>
        <CopySimple size={13} className="text-text-tertiary" />
        {isMulti ? `Duplicate ${targetIds.length} layers` : 'Duplicate'}
      </ContextMenuItem>

      {/* Move to group sub-menu */}
      {groups.length > 0 && (
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <ArrowSquareIn size={13} className="text-text-tertiary" />
            Move to group
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {layer.groupId && !isMulti && (
              <>
                <ContextMenuItem onClick={() => handleMoveToGroup(null)}>
                  <ArrowSquareOut size={13} className="text-text-tertiary" />
                  Remove from group
                </ContextMenuItem>
                <ContextMenuSeparator />
              </>
            )}
            {isMulti && (
              <>
                <ContextMenuItem onClick={() => handleMoveToGroup(null)}>
                  <ArrowSquareOut size={13} className="text-text-tertiary" />
                  Remove from group
                </ContextMenuItem>
                <ContextMenuSeparator />
              </>
            )}
            {groups.map((g) => (
              <ContextMenuItem
                key={g.id}
                disabled={!isMulti && layer.groupId === g.id}
                onClick={() => handleMoveToGroup(g.id)}
              >
                {g.name}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
      )}

      <ContextMenuSeparator />

      <ContextMenuItem
        onClick={handleDelete}
        className="text-error hover:text-error focus:text-error"
      >
        <Trash size={13} />
        {isMulti ? `Delete ${targetIds.length} layers` : 'Delete layer'}
      </ContextMenuItem>
    </ContextMenuContent>
  );
}
